import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  marketScreenerApi,
  MarketScreenerType,
  MarketScreenerResult,
  MarketScreenerTypeRequest,
  MarketScreenerResultRequest,
} from '../../../services/adminApi.ts';
import './AdminMarketScreener.css';

const CATEGORIES = ['Price Action', 'Moving Averages', 'Volume', 'Momentum'];
const CAP_NAMES  = ['Large Cap', 'Mid Cap', 'Small Cap', 'Micro Cap'];
const PAGE_SIZE  = 20;

// ── Type Form ─────────────────────────────────────────────────────────────────

interface TypeFormProps {
  initial?: MarketScreenerType | null;
  onSave: (req: MarketScreenerTypeRequest) => void;
  onCancel: () => void;
  saving: boolean;
}

const TypeForm: React.FC<TypeFormProps> = ({ initial, onSave, onCancel, saving }) => {
  const [code,     setCode]     = useState(initial?.code        ?? '');
  const [name,     setName]     = useState(initial?.name        ?? '');
  const [desc,     setDesc]     = useState(initial?.description ?? '');
  const [category, setCategory] = useState(initial?.category    ?? CATEGORIES[0]);
  const [sortOrder,setSortOrder]= useState(String(initial?.sortOrder ?? 0));
  const [isActive, setIsActive] = useState(initial?.isActive    ?? true);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim() || !name.trim()) { toast.error('Code and Name are required'); return; }
    onSave({ code, name, description: desc || undefined, category, isActive, sortOrder: parseInt(sortOrder) || 0 });
  };

  return (
    <form className="ms-type-form" onSubmit={handleSubmit}>
      <div className="ms-form-row">
        <label>Code
          <input value={code} onChange={e => setCode(e.target.value.toUpperCase())}
            placeholder="e.g. 52W_HIGH" disabled={!!initial} required />
        </label>
        <label>Sort Order
          <input type="number" value={sortOrder} onChange={e => setSortOrder(e.target.value)} min="0" />
        </label>
      </div>
      <label>Name
        <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. 52 Week High" required />
      </label>
      <label>Category
        <select value={category} onChange={e => setCategory(e.target.value)}>
          {CATEGORIES.map(c => <option key={c}>{c}</option>)}
        </select>
      </label>
      <label>Description
        <textarea rows={2} value={desc} onChange={e => setDesc(e.target.value)} placeholder="Optional" />
      </label>
      <label className="ms-checkbox-row">
        <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} />
        Active
      </label>
      <div className="ms-form-actions">
        <button type="button" className="ms-btn-cancel" onClick={onCancel}>Cancel</button>
        <button type="submit" className="ms-btn-save" disabled={saving}>
          {saving ? 'Saving…' : initial ? 'Update' : 'Create'}
        </button>
      </div>
    </form>
  );
};

// ── Result Row ────────────────────────────────────────────────────────────────

const ResultRow: React.FC<{ r: MarketScreenerResult; onDelete: (id: number) => void }> = ({ r, onDelete }) => (
  <tr>
    <td>{r.rankOrder ?? '—'}</td>
    <td><span className="ms-symbol">{r.symbol}</span></td>
    <td className="ms-company">{r.companyName ?? '—'}</td>
    <td>{r.exchange}</td>
    <td className="ms-num">{r.ltp != null ? `₹${r.ltp.toLocaleString('en-IN')}` : '—'}</td>
    <td className={`ms-num ${r.changePct != null ? (r.changePct >= 0 ? 'ms-green' : 'ms-red') : ''}`}>
      {r.changePct != null ? `${r.changePct >= 0 ? '+' : ''}${r.changePct}%` : '—'}
    </td>
    <td className="ms-num">{r.volume != null ? r.volume.toLocaleString('en-IN') : '—'}</td>
    <td className="ms-num">{r.week52High != null ? `₹${r.week52High}` : '—'}</td>
    <td className="ms-num">{r.week52Low  != null ? `₹${r.week52Low}`  : '—'}</td>
    <td className="ms-num">{r.marketCap  != null ? `₹${r.marketCap.toLocaleString('en-IN')}Cr` : '—'}</td>
    <td>{r.marketCapName ?? '—'}</td>
    <td>{r.sector   ?? '—'}</td>
    <td>{r.industry ?? '—'}</td>
    <td>
      <button className="ms-del-btn" onClick={() => onDelete(r.id)} title="Delete">✕</button>
    </td>
  </tr>
);

// ── Add Result Form ───────────────────────────────────────────────────────────

interface AddResultFormProps {
  screenerId: number;
  onSave: (req: MarketScreenerResultRequest) => void;
  onCancel: () => void;
  saving: boolean;
}

const AddResultForm: React.FC<AddResultFormProps> = ({ screenerId, onSave, onCancel, saving }) => {
  const [symbol,      setSymbol]      = useState('');
  const [companyName, setCompanyName] = useState('');
  const [exchange,    setExchange]    = useState('NSE');
  const [ltp,         setLtp]         = useState('');
  const [changePct,   setChangePct]   = useState('');
  const [volume,      setVolume]      = useState('');
  const [w52h,        setW52h]        = useState('');
  const [w52l,        setW52l]        = useState('');
  const [marketCap,   setMarketCap]   = useState('');
  const [capName,     setCapName]     = useState('');
  const [sector,      setSector]      = useState('');
  const [industry,    setIndustry]    = useState('');
  const [rankOrder,   setRankOrder]   = useState('');
  const [asOfDate,    setAsOfDate]    = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!symbol.trim()) { toast.error('Symbol is required'); return; }
    onSave({
      screenerId,
      symbol,
      companyName: companyName || undefined,
      exchange,
      ltp:          ltp          ? parseFloat(ltp)         : undefined,
      changePct:    changePct    ? parseFloat(changePct)   : undefined,
      volume:       volume       ? parseInt(volume)        : undefined,
      week52High:   w52h         ? parseFloat(w52h)        : undefined,
      week52Low:    w52l         ? parseFloat(w52l)        : undefined,
      marketCap:    marketCap    ? parseFloat(marketCap)   : undefined,
      marketCapName: capName     || undefined,
      sector:        sector      || undefined,
      industry:      industry    || undefined,
      rankOrder:     rankOrder   ? parseInt(rankOrder)     : undefined,
      asOfDate:      asOfDate    || undefined,
    });
  };

  return (
    <form className="ms-add-result-form" onSubmit={handleSubmit}>
      <div className="ms-form-row">
        <label>Symbol *
          <input value={symbol} onChange={e => setSymbol(e.target.value.toUpperCase())} placeholder="RELIANCE" required />
        </label>
        <label>Exchange
          <select value={exchange} onChange={e => setExchange(e.target.value)}>
            <option>NSE</option><option>BSE</option>
          </select>
        </label>
        <label>Rank
          <input type="number" value={rankOrder} onChange={e => setRankOrder(e.target.value)} placeholder="1" min="1" />
        </label>
      </div>
      <div className="ms-form-row">
        <label>Company Name
          <input value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="Reliance Industries Ltd" />
        </label>
        <label>As of Date
          <input type="date" value={asOfDate} onChange={e => setAsOfDate(e.target.value)} />
        </label>
      </div>
      <div className="ms-form-row ms-form-row--5">
        <label>LTP (₹)
          <input type="number" value={ltp} onChange={e => setLtp(e.target.value)} placeholder="0.00" step="0.01" />
        </label>
        <label>Change %
          <input type="number" value={changePct} onChange={e => setChangePct(e.target.value)} placeholder="0.00" step="0.01" />
        </label>
        <label>Volume
          <input type="number" value={volume} onChange={e => setVolume(e.target.value)} placeholder="0" />
        </label>
        <label>52W High (₹)
          <input type="number" value={w52h} onChange={e => setW52h(e.target.value)} placeholder="0.00" step="0.01" />
        </label>
        <label>52W Low (₹)
          <input type="number" value={w52l} onChange={e => setW52l(e.target.value)} placeholder="0.00" step="0.01" />
        </label>
      </div>
      <div className="ms-form-row ms-form-row--4">
        <label>Market Cap (Cr)
          <input type="number" value={marketCap} onChange={e => setMarketCap(e.target.value)} placeholder="0.00" step="0.01" />
        </label>
        <label>Cap Category
          <select value={capName} onChange={e => setCapName(e.target.value)}>
            <option value="">— select —</option>
            {CAP_NAMES.map(c => <option key={c}>{c}</option>)}
          </select>
        </label>
        <label>Sector
          <input value={sector} onChange={e => setSector(e.target.value)} placeholder="Technology" />
        </label>
        <label>Industry
          <input value={industry} onChange={e => setIndustry(e.target.value)} placeholder="IT Services" />
        </label>
      </div>
      <div className="ms-form-actions">
        <button type="button" className="ms-btn-cancel" onClick={onCancel}>Cancel</button>
        <button type="submit" className="ms-btn-save" disabled={saving}>
          {saving ? 'Adding…' : 'Add Stock'}
        </button>
      </div>
    </form>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────

const AdminMarketScreener: React.FC = () => {
  const navigate = useNavigate();

  const [types,         setTypes]         = useState<MarketScreenerType[]>([]);
  const [selected,      setSelected]      = useState<MarketScreenerType | null>(null);
  const [results,       setResults]       = useState<MarketScreenerResult[]>([]);
  const [totalPages,    setTotalPages]    = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [page,          setPage]          = useState(1);

  const [typesLoading,   setTypesLoading]   = useState(false);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [saving,         setSaving]         = useState(false);

  const [showTypeForm,   setShowTypeForm]   = useState(false);
  const [editingType,    setEditingType]    = useState<MarketScreenerType | null>(null);
  const [showResultForm, setShowResultForm] = useState(false);

  // Load screener types
  useEffect(() => {
    setTypesLoading(true);
    marketScreenerApi.getTypes()
      .then(r => setTypes(r.data.data))
      .catch(() => toast.error('Failed to load screener types'))
      .finally(() => setTypesLoading(false));
  }, []);

  // Load results when selected type or page changes
  const loadResults = useCallback((screenerId: number, p: number) => {
    setResultsLoading(true);
    marketScreenerApi.getResults(screenerId, p, PAGE_SIZE)
      .then(r => {
        const paged = r.data.data;
        setResults(paged.content);
        setTotalPages(paged.totalPages);
        setTotalElements(paged.totalElements);
      })
      .catch(() => toast.error('Failed to load results'))
      .finally(() => setResultsLoading(false));
  }, []);

  useEffect(() => {
    if (selected) loadResults(selected.id, page);
  }, [selected, page, loadResults]);

  const selectType = (t: MarketScreenerType) => {
    setSelected(t);
    setPage(1);
    setResults([]);
    setShowResultForm(false);
  };

  // Type CRUD
  const handleSaveType = async (req: MarketScreenerTypeRequest) => {
    setSaving(true);
    try {
      if (editingType) {
        const r = await marketScreenerApi.updateType(editingType.id, req);
        setTypes(prev => prev.map(t => t.id === editingType.id ? r.data.data : t));
        if (selected?.id === editingType.id) setSelected(r.data.data);
        toast.success('Screener updated');
      } else {
        const r = await marketScreenerApi.createType(req);
        setTypes(prev => [...prev, r.data.data]);
        toast.success('Screener created');
      }
      setShowTypeForm(false);
      setEditingType(null);
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? 'Failed to save screener');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteType = async (t: MarketScreenerType) => {
    if (!window.confirm(`Delete screener "${t.name}" and all its results?`)) return;
    try {
      await marketScreenerApi.deleteType(t.id);
      setTypes(prev => prev.filter(x => x.id !== t.id));
      if (selected?.id === t.id) { setSelected(null); setResults([]); }
      toast.success('Screener deleted');
    } catch {
      toast.error('Failed to delete screener');
    }
  };

  // Result CRUD
  const handleAddResult = async (req: MarketScreenerResultRequest) => {
    setSaving(true);
    try {
      await marketScreenerApi.addResult(req);
      toast.success(`${req.symbol} added`);
      setShowResultForm(false);
      if (selected) loadResults(selected.id, page);
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? 'Failed to add result');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteResult = async (id: number) => {
    try {
      await marketScreenerApi.deleteResult(id);
      setResults(prev => prev.filter(r => r.id !== id));
      setTotalElements(n => n - 1);
      toast.success('Removed');
    } catch {
      toast.error('Failed to delete result');
    }
  };

  const handleClearResults = async () => {
    if (!selected) return;
    if (!window.confirm(`Clear ALL results for "${selected.name}"?`)) return;
    try {
      await marketScreenerApi.clearResults(selected.id);
      setResults([]);
      setTotalElements(0);
      setTotalPages(0);
      toast.success('All results cleared');
    } catch {
      toast.error('Failed to clear results');
    }
  };

  // Group types by category
  const grouped = types.reduce<Record<string, MarketScreenerType[]>>((acc, t) => {
    (acc[t.category] = acc[t.category] ?? []).push(t);
    return acc;
  }, {});

  return (
    <div className="ms-page">

      {/* Header */}
      <div className="ms-header">
        <button className="ms-back-btn" onClick={() => navigate(-1)}>←</button>
        <div>
          <h1 className="ms-title">Market Screener</h1>
          <p className="ms-subtitle">Manage screener types and stock lists</p>
        </div>
      </div>

      <div className="ms-layout">

        {/* Left panel — screener types */}
        <div className="ms-left">
          <div className="ms-left-header">
            <span className="ms-left-title">Screener Types</span>
            <button className="ms-add-type-btn" onClick={() => { setEditingType(null); setShowTypeForm(true); }}>+ New</button>
          </div>

          {showTypeForm && !editingType && (
            <div className="ms-type-form-wrap">
              <TypeForm onSave={handleSaveType} onCancel={() => setShowTypeForm(false)} saving={saving} />
            </div>
          )}

          {typesLoading ? (
            <div className="ms-loading">Loading…</div>
          ) : types.length === 0 ? (
            <div className="ms-empty-types">No screeners yet. Click <strong>+ New</strong> to create one.</div>
          ) : (
            Object.entries(grouped).map(([cat, items]) => (
              <div key={cat} className="ms-category-group">
                <div className="ms-category-label">{cat}</div>
                {items.map(t => (
                  <div key={t.id}>
                    <div
                      className={`ms-type-item ${selected?.id === t.id ? 'active' : ''} ${!t.isActive ? 'inactive' : ''}`}
                      onClick={() => selectType(t)}
                    >
                      <div className="ms-type-info">
                        <span className="ms-type-name">{t.name}</span>
                        <span className="ms-type-code">{t.code}</span>
                      </div>
                      <div className="ms-type-actions">
                        <button onClick={e => { e.stopPropagation(); setEditingType(t); setShowTypeForm(true); }} title="Edit">✎</button>
                        <button onClick={e => { e.stopPropagation(); handleDeleteType(t); }} title="Delete" className="ms-del">✕</button>
                      </div>
                    </div>
                    {showTypeForm && editingType?.id === t.id && (
                      <div className="ms-type-form-wrap">
                        <TypeForm
                          initial={editingType}
                          onSave={handleSaveType}
                          onCancel={() => { setShowTypeForm(false); setEditingType(null); }}
                          saving={saving}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))
          )}
        </div>

        {/* Right panel — results */}
        <div className="ms-right">
          {!selected ? (
            <div className="ms-no-selection">
              <span>👈</span>
              <p>Select a screener from the left to view and manage its stock list</p>
            </div>
          ) : (
            <>
              <div className="ms-right-header">
                <div className="ms-right-title-row">
                  <h2 className="ms-right-title">{selected.name}</h2>
                  <span className="ms-right-code">{selected.code}</span>
                  {!selected.isActive && <span className="ms-inactive-badge">Inactive</span>}
                </div>
                <div className="ms-right-actions">
                  <span className="ms-result-count">{totalElements} stocks</span>
                  {totalElements > 0 && (
                    <button className="ms-clear-btn" onClick={handleClearResults}>Clear All</button>
                  )}
                  <button className="ms-add-result-btn" onClick={() => setShowResultForm(v => !v)}>
                    {showResultForm ? 'Cancel' : '+ Add Stock'}
                  </button>
                </div>
              </div>

              {showResultForm && (
                <AddResultForm
                  screenerId={selected.id}
                  onSave={handleAddResult}
                  onCancel={() => setShowResultForm(false)}
                  saving={saving}
                />
              )}

              {resultsLoading ? (
                <div className="ms-loading">Loading stocks…</div>
              ) : results.length === 0 ? (
                <div className="ms-empty-results">
                  No stocks in this screener yet. Click <strong>+ Add Stock</strong> to add one.
                </div>
              ) : (
                <>
                  <div className="ms-table-wrap">
                    <table className="ms-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Symbol</th>
                          <th>Company</th>
                          <th>Exch</th>
                          <th>LTP</th>
                          <th>Chg %</th>
                          <th>Volume</th>
                          <th>52W H</th>
                          <th>52W L</th>
                          <th>Mkt Cap (Cr)</th>
                          <th>Cap Cat</th>
                          <th>Sector</th>
                          <th>Industry</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.map(r => (
                          <ResultRow key={r.id} r={r} onDelete={handleDeleteResult} />
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="ms-pagination">
                      <button disabled={page === 1} onClick={() => setPage(1)}>«</button>
                      <button disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
                      <span className="ms-page-info">Page {page} of {totalPages}</span>
                      <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
                      <button disabled={page === totalPages} onClick={() => setPage(totalPages)}>»</button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminMarketScreener;
